function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compact, countBy, isEmpty, mapValues, omitBy, isUndefined } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { TableList, Page, PageEmptyState, PageErrorState, FilterPickerGroup, Searchbox, PageContentHeader, Spinner, ConfirmationModal, Typography, DropdownMenu, ChipButton, Button, useStateInRef, DropdownMenuItem } from 'seagull';
import { DownloadLiner24, ExportLiner24, MailSentLiner24, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { getAgreementPdfUrl } from "../../business-logic/relationshipLogic";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { ErrorText } from "../../constants";
import { useOverlayContext, useIoContext, useStore, useStoreActions } from "../../contexts";
import { useDuplicateProposal } from "../../hooks/reactQuery/mutations/useDuplicateProposal";
import { useRedirectToProposal } from "../../hooks/useCreateAgreement";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useEnterAccountantAccess } from "../../hooks/useEnterAccountantAccess";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useOpenEditAgreementModal } from "../../hooks/useOpenEditAgreementModal";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { useTerminateAgreement } from "../../hooks/useTerminateAgreement";
import { useBulkActionModal } from "../../hooks/useTerminateAgreement/useBulkActionModal";
import { useVerifyPlaidBankAccount } from "../../hooks/useVerifyPlaidBankAccount";
import { Intent } from "../../infra/access-control";
import { handlePromise, handleRequest } from "../../infra/utils";
import { relationshipModel, relationshipSummaryModel } from "../../models";
import { PlaidBankAccountStatusTypes } from "../../models/payment-method";
import { useSubscribeToBillingObservable } from "../../providers/BillingObservableProvider";
import { selectors } from "../../store/state-selectors";
import { useAppConfig, useStoreStatic } from "../../store/store-context";
import { helpArticles } from "../../utils/helpArticles";
import pluralize from "../../utils/pluralize";
import { buildRelationshipsParamsFilter } from "../../utils/relationships-params-filter-builder";
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import PageLoadingState from "../../components/PageLoadingState";
import SeeWhatItsLike from "../../components/SeeWhatItsLike/SeeWhatItsLike";
import styles from "./AgreementsDashboard.module.scss";
import { sortAgreements, HeaderColId } from "./AgreementsDashboard.utils";
import { serializeFilter, filtersConfig, FilterSelector, FilterType, deserializeFiltersFromURL, defaultAgreementStatusFilter } from "./agreementsDashboardFilters";
import AgreementsDashboardRow from "./AgreementsDashboardRow";
import ChangeAssigneeModal from "./ChangeAssigneeModal";
import { NoAgreementsIcon } from "./NoAgreementsIcon";
import RenameAgreementModal from "./RenameAgreementModal";
import img1 from "./SeeWhatItIsLikeImages/1.png";
import img2 from "./SeeWhatItIsLikeImages/2.png";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const title = 'Agreements';
const relationshipLoader = 'getRelationship';
function getTableDefs(isVendor) {
  const defs = compact([{
    headerItem: {
      id: HeaderColId.companyName,
      value: 'Company name'
    },
    columnDef: {
      width: 220,
      sortable: true
    }
  }, {
    headerItem: {
      id: HeaderColId.status,
      value: 'Status'
    },
    columnDef: {
      width: 128,
      sortable: true
    }
  }, isVendor && {
    headerItem: {
      id: HeaderColId.assignee,
      value: 'Assignee'
    },
    columnDef: {
      width: [130, 160],
      sortable: true
    }
  }, {
    headerItem: {
      id: HeaderColId.effectiveDate,
      value: 'Effective date'
    },
    columnDef: {
      width: [112, 120],
      sortable: true
    }
  }, {
    headerItem: {
      id: HeaderColId.paymentMethod,
      value: 'Payment method'
    },
    columnDef: {
      width: 190,
      sortable: true
    }
  }, {
    headerItem: {
      id: HeaderColId.lastBilled,
      value: 'Last Billed'
    },
    columnDef: {
      width: 100,
      sortable: true
    }
  }]);
  return {
    columnDefs: defs.map(d => d.columnDef),
    headerItems: defs.map(d => d.headerItem)
  };
}
export const AgreementsDashboardTestIds = pathgen('AgreementsDashboardTestIds');
export function ActualAgreementsDashboard(props) {
  const {
    data: _data,
    isRefreshing,
    search,
    filters,
    onChangeFilter,
    refresh,
    onChangeSearch
  } = props;
  const [data, setData] = useState(_data);
  useEffect(() => setData(_data), [_data]);
  const overlayContext = useOverlayContext();
  const {
    openNewTab
  } = useIoContext();
  const isVendor = useStore(selectors.user.isVendor);
  const {
    inputDebounceTime
  } = useAppConfig();
  const businessesWithAccountantAccess = useStore(selectors.user.businessesWithAccountantAccess);
  const pdfHost = useStore(selectors.pdfHost);
  const {
    api,
    logBI
  } = useStoreActions();
  const openEditModeModal = useOpenEditAgreementModal();
  const {
    headerItems,
    columnDefs
  } = useMemo(() => getTableDefs(isVendor), [isVendor]);
  const sortIds = useMemo(() => headerItems.map(item => item.id), [headerItems]);
  const defaultSort = useMemo(() => ({
    colIndex: 3,
    sort: 'desc'
  }), []);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    redirectToProposal
  } = useRedirectToProposal();
  const {
    duplicateProposal
  } = useDuplicateProposal({
    onSuccess: newDraftId => redirectToProposal('agreement', newDraftId),
    withPageLoader: true
  });
  const {
    terminateAgreementById
  } = useTerminateAgreement();
  const enterAccountantAccess = useEnterAccountantAccess();
  const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
  const scrollElRef = useRef(null);
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11252, 0);
  const {
    doBulkAction
  } = useBulkActionModal();
  const {
    getState
  } = useStoreStatic();
  const {
    EXPORT_AGREEMENTS_TO_CSV
  } = useFeatureFlags();
  const businessId = useStore(selectors.user.businessId);
  const [initialFilters, setInitialFilters] = useState(Object.values(FilterType).filter(type => filtersConfig[type].hasValue(filters[type])));
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selected, setSelected] = useState({});
  const getSelected = useStateInRef(selected);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const numSelected = useMemo(() => {
    var _countBy$true;
    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  const allFilters = initialFilters.concat(selectedFilters);
  const agreements = useMemo(() => {
    return sortAgreements({
      agreements: data,
      sort,
      isVendor,
      headerItems
    });
  }, [data, sort, isVendor, headerItems]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: agreements,
    scrollElRef
  });
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  useEffect(() => {
    // this unselects any agreement that is not found in the newly fetched list of agreements
    if (!data || isEmpty(getSelected())) return;
    const ids = data.map(agreement => agreement.id);
    const newSelected = mapValues(getSelected(), (origValue, agreementId) => ids.includes(agreementId) ? origValue : false);
    setSelected(newSelected);
  }, [data]);
  const onToggleAllSelection = useCallback(selectAll => {
    setSelected(agreements.reduce((acc, _ref) => {
      let {
        id
      } = _ref;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }, [agreements]);
  function onVerifyPaymentMethod(agreement) {
    if (!agreement.fundingSource) return;
    if (agreement.fundingSource.isPlaidVerification()) {
      verifyPlaidBankAccount({
        fundingSourceId: agreement.fundingSource.id(),
        relationshipId: agreement.id,
        onVerify() {
          // Optimistic UI.
          const newData = data.map(r => r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.ManuallyVerified) : r);
          setData(newData);
        },
        onVerifyFailed() {
          // Optimistic UI.
          const newData = data.map(r => r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.VerificationFailed) : r);
          setData(newData);
        }
      });
    }
  }
  const onSortChange = sort => {
    logBI(11250, {
      column: sort ? sortIds[sort.colIndex] : 'none'
    });
    changeSort(sort);
  };
  const onClearSearch = () => {
    onChangeSearch('');
    debouncedLogSearchToBI.onClear();
  };
  const onDownloadPDF = agreement => {
    openNewTab(getAgreementPdfUrl(agreement.id, pdfHost));
  };
  const onTerminateAgreement = agreement => {
    terminateAgreementById({
      id: agreement.id,
      postAction: () => {
        refresh();
      }
    });
  };
  const onEditAgreement = agreement => {
    openEditModeModal({
      relationshipId: agreement.id,
      onUpdate: refresh
    });
  };
  const onDuplicateAgreement = agreement => {
    duplicateProposal({
      relationshipId: agreement.id,
      draftId: agreement.draftId
    });
  };
  function addFilter(type) {
    setSelectedFilters(selectedFilters.concat(type));
  }
  function removeFilter(type) {
    setInitialFilters(initialFilters.filter(t => t !== type));
    setSelectedFilters(selectedFilters.filter(t => t !== type));
    onChangeFilter(type, filtersConfig[type].getEmptyValue());
  }
  function getSelectedAgreements() {
    return Object.entries(selected).filter(_ref2 => {
      let [, isSelected] = _ref2;
      return isSelected;
    }).map(_ref3 => {
      let [agreementId, _] = _ref3;
      return agreementId;
    });
  }
  async function bulkRequestPaymentMethod(selectedAgreements) {
    logBI(11254, {
      count: selectedAgreements.length
    });
    await doBulkAction({
      bulkItemIds: selectedAgreements,
      confirmation: {
        title: 'Request Payment Method',
        primaryButtonLabel: 'Send Request',
        content: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsxs(Typography, {
            children: ["By selecting \"Send Request\", all clients of the selected", ' ', /*#__PURE__*/_jsx("strong", {
              children: selectedAgreements.length
            }), " agreements will receive an email asking them to set up their payment details in Anchor."]
          }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Typography, {
            children: "Consider sending a personal message to clients still transitioning to Anchor to communicate the purpose of this request."
          }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(ExternalLinkButton, {
            openInNewTab: true,
            href: helpArticles.requestPaymentMethod(),
            children: "Learn more about Request Payment Method"
          })]
        })
      },
      progressTitle: 'Request Payment Method',
      actionName: 'sent',
      itemName: 'payment method request',
      onConfirmed: () => {
        logBI(11255, {
          count: selectedAgreements.length
        });
      },
      canProcessItem: itemId => agreements.find(a => a.id === itemId && !relationshipModel.isTerminated(a)) != null,
      processSingleItem: agreementId => handlePromise(api.relationship.requestPaymentMethod(agreementId)),
      onDone: failedAgreementIds => {
        setSelected(failedAgreementIds.reduce((acc, agreementId) => _objectSpread(_objectSpread({}, acc), {}, {
          [agreementId]: true
        }), {}));
      }
    });
  }
  async function onRenameAgreement(agreement, onSuccess) {
    overlayContext.startLoader(relationshipLoader);
    const relationship = await handleRequest(api.relationship.get(agreement.id));
    overlayContext.stopLoader(relationshipLoader);
    if (!relationship.success) {
      overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'get-agr-error');
    } else {
      overlayContext.showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: p => /*#__PURE__*/_jsx(RenameAgreementModal, _objectSpread(_objectSpread({}, p), {}, {
          relationship: relationship.data,
          onSuccess: onSuccess
        }))
      });
    }
  }
  const openImportAgreementsModal = () => {
    overlayContext.showModal({
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          textAlignCenter: false,
          primaryButtonLabel: "Close",
          onPrimaryButtonClick: dismissModal,
          onCloseButtonClick: dismissModal,
          title: "Import agreements",
          primaryButtonTestid: "close-import-agreements-modal",
          content: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Typography, {
              children: "Need to import agreements into Anchor that were signed elsewhere?"
            }), /*#__PURE__*/_jsxs(Typography, {
              children: [/*#__PURE__*/_jsx(ExternalLinkButton, {
                openInNewTab: true,
                href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                children: "Complete this form"
              }), ' ', "and our team will reach out with the next steps."]
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "paragraph2",
              style: {
                marginTop: '16px'
              },
              children: "Please note: This service is currently available for requests of 30 agreements or more."
            })]
          })
        });
      }
    });
  };
  function changeAssignee(agreement) {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(ChangeAssigneeModal, _objectSpread({
        relationship: agreement,
        onSuccess: refresh
      }, p))
    });
  }
  const exportToCsv = () => {
    const mgmtHost = selectors.mgmtHost(getState());
    const filterParams = buildRelationshipsParamsFilter({
      type: 'agreement',
      isVendor,
      search,
      filters
    });
    const cleanedFilterParams = omitBy(filterParams, isUndefined);
    const searchParams = new URLSearchParams(cleanedFilterParams).toString();
    openNewTab("".concat(mgmtHost, "/business/").concat(businessId, "/relationships-summary-mv/export-csv?").concat(searchParams));
  };
  return data.length > 0 || allFilters.length > 0 || search.length || isRefreshing ? /*#__PURE__*/_jsx(Page, {
    "data-testid": "agreements-dashboard",
    header: title,
    ref: scrollElRef,
    actions: isVendor && /*#__PURE__*/_jsxs(_Fragment, {
      children: [EXPORT_AGREEMENTS_TO_CSV ? /*#__PURE__*/_jsxs(DropdownMenu, {
        "data-testid": AgreementsDashboardTestIds.MoreActions.Root(),
        target: /*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": AgreementsDashboardTestIds.MoreActions.Button(),
          suffixIcon: /*#__PURE__*/_jsx(WideArrowDownward, {}),
          children: "More Actions"
        }),
        children: [/*#__PURE__*/_jsx(DropdownMenuItem, {
          "data-testid": AgreementsDashboardTestIds.MoreActions.ImportAgreementsBtn(),
          iconStart: /*#__PURE__*/_jsx(DownloadLiner24, {}),
          onClick: () => openImportAgreementsModal(),
          children: "Import agreements"
        }), /*#__PURE__*/_jsx(DropdownMenuItem, {
          intent: Intent.ExportAgreementsToCsv,
          iconStart: /*#__PURE__*/_jsx(ExportLiner24, {}),
          "data-testid": AgreementsDashboardTestIds.MoreActions.ExportAgreementsBtn(),
          onClick: exportToCsv,
          children: "Export agreements"
        })]
      }) : /*#__PURE__*/_jsx(Button, {
        skin: "primaryOutline",
        "data-testid": "import-agreements-btn",
        onClick: () => openImportAgreementsModal(),
        children: "Import"
      }), /*#__PURE__*/_jsx(CreateProposalButton, {}, "new-agreement-button")]
    }),
    subheader: EXPORT_AGREEMENTS_TO_CSV ? undefined : pluralize('agreement', data.length, true),
    onScroll: onScroll,
    children: /*#__PURE__*/_jsxs(TableList.Provider, {
      hasActionColumn: true,
      enableRowSelection: isVendor,
      columnDefs: columnDefs,
      sort: sort,
      selectedRows: selected,
      onSortChange: onSortChange,
      onSelectionChange: setSelected,
      numRows: agreements.length,
      numSelectedRows: numSelected,
      onToggleAllSelection: onToggleAllSelection,
      children: [/*#__PURE__*/_jsxs(PageContentHeader, {
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.agreementsFilters,
          children: /*#__PURE__*/_jsxs(FilterPickerGroup, {
            flexWrap: true,
            "data-testid": "agreements-dashboard-filters",
            children: [/*#__PURE__*/_jsx(Searchbox, {
              registerHotkey: registerSearchBoxHotkey,
              value: search,
              "data-testid": "agreements-dashboard-search",
              debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
              onClear: onClearSearch,
              onChange: event => {
                const searchStr = event.target.value;
                onChangeSearch(searchStr);
                debouncedLogSearchToBI.onChange(searchStr);
              }
            }), allFilters.map(type => /*#__PURE__*/createElement(filtersConfig[type].component, {
              key: type,
              startOpen: selectedFilters.includes(type),
              value: filters[type],
              onChange: v => onChangeFilter(type, v),
              onRemove: () => removeFilter(type),
              onInputFocus: () => setRegisterSearchBoxHotkey(false),
              onInputBlur: () => setRegisterSearchBoxHotkey(true)
            })), /*#__PURE__*/_jsx(FilterSelector, {
              selected: allFilters,
              onSelect: addFilter
            }), isVendor && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Typography, {
                className: styles.selectedItems,
                "data-testid": "agreement-dashboard-bulk-counter",
                children: numSelected > 0 ? "".concat(pluralize('agreement', numSelected, true), " selected") : ''
              }), /*#__PURE__*/_jsx(DropdownMenu, {
                onOpenChange: setBulkActionsOpen,
                target: /*#__PURE__*/_jsx(ChipButton, {
                  "data-testid": "agreement-dashboard-bulk-actions",
                  suffixIcon: bulkActionsOpen ? /*#__PURE__*/_jsx(WideArrowUpward, {}) : /*#__PURE__*/_jsx(WideArrowDownward, {}),
                  highlight: bulkActionsOpen,
                  disabled: numSelected === 0,
                  children: "Bulk actions"
                }),
                children: /*#__PURE__*/_jsx(DropdownMenu.Item, {
                  iconStart: /*#__PURE__*/_jsx(MailSentLiner24, {}),
                  "data-testid": "agreement-dashboard-bulk-request-payment-method",
                  onClick: () => bulkRequestPaymentMethod(getSelectedAgreements()),
                  disabled: !isVendor,
                  children: "Request payment method"
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph1",
          weight: "light",
          "data-testid": AgreementsDashboardTestIds.TableHeader.AgreementsCounter(),
          className: styles.agreementsCounter,
          children: pluralize('agreement', data.length, true)
        }), /*#__PURE__*/_jsx(TableList.Header, {
          items: headerItems,
          "data-testid": "agreements-dashboard-table-header"
        })]
      }), isRefreshing ? /*#__PURE__*/_jsx("div", {
        className: styles.loading,
        "data-testid": "agreements-table-loading",
        children: /*#__PURE__*/_jsx(Spinner, {})
      }) : agreements.length ? paginatedData.map((agreement, index) => /*#__PURE__*/_jsx(AgreementsDashboardRow, {
        agreement: agreement,
        hasAccountantAccess: isVendor && businessesWithAccountantAccess.has(relationshipSummaryModel.customerBusinessId(agreement)),
        isVendor: isVendor,
        index: index,
        onVerifyPaymentMethod: onVerifyPaymentMethod,
        onEditAgreement: onEditAgreement,
        onDownloadPDF: onDownloadPDF,
        onDuplicateAgreement: onDuplicateAgreement,
        onTerminateAgreement: onTerminateAgreement,
        onEnterAccountantAccess: enterAccountantAccess,
        onRenameAgreement: onRenameAgreement,
        onChangeAssignee: changeAssignee
      }, agreement.id)) : /*#__PURE__*/_jsx(PageEmptyState, {
        illustration: /*#__PURE__*/_jsx(NoAgreementsIcon, {}),
        title: "No agreements match your request",
        message: "Try adjusting your search",
        "data-testid": "agreements-empty-table"
      })]
    })
  }) : /*#__PURE__*/_jsx(Page, {
    header: title,
    loading: isRefreshing,
    children: /*#__PURE__*/_jsx(PageEmptyState, {
      "data-testid": "agreements-empty",
      illustration: /*#__PURE__*/_jsx(NoAgreementsIcon, {}),
      title: "Make it official with an online agreement",
      message: "The Anchor agreement can always be updated, is accessible online, and serves as a single source of truth between you and your clients.",
      actions: isVendor && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SeeWhatItsLike, {
          steps: [{
            title: 'A single place to maintain client information',
            subtitle: 'All aspects of your agreement and relationship history live here, online, equally visible to you and your client.',
            image: {
              src: img1,
              width: 1246,
              height: 748
            }
          }, {
            title: 'Hands-free relationship management ',
            subtitle: 'You and your clients will be updated on any changes or required action.',
            image: {
              src: img2,
              width: 1246,
              height: 748
            }
          }],
          "data-testid": "agreements-dashboard"
        }), /*#__PURE__*/_jsx(CreateProposalButton, {
          text: "Create a new agreement",
          hidePrefix: true,
          "data-testid": "agreements-empty-new-btn"
        })]
      })
    })
  });
}
export default function AgreementsDashboard() {
  const {
    api
  } = useStoreActions();
  const businessId = useStore(selectors.user.businessId);
  const isVendor = useStore(selectors.user.isVendor);
  const shouldRefresh = useRef(false);
  const refreshRef = useRef(() => {});
  const {
    urlState,
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    },
    agreementStatus: {
      multi: true,
      applyDefaultOnlyOnMount: true,
      defaultValue: defaultAgreementStatusFilter
    },
    effectiveDate: {
      defaultValue: ''
    },
    lastBillingDate: {
      defaultValue: ''
    },
    paymentMethod: {
      multi: true
    },
    completion: {
      multi: true
    },
    recurrence: {
      multi: true
    },
    hasCredit: {
      multi: true
    },
    automatic: {
      multi: true
    },
    billingTrigger: {
      multi: true
    },
    serviceNames: {
      multi: true
    },
    servicePriceIncreaseState: {
      multi: true
    },
    hasAmendments: {
      multi: true
    }
  });
  const {
    search
  } = urlState;
  const deserializedFilters = useMemo(() => deserializeFiltersFromURL(urlState), [urlState]);
  async function refreshData() {
    if (!businessId) return Promise.resolve([]);
    return api.relationship.getSummaries({
      businessId,
      type: 'agreement',
      isVendor,
      search,
      filters: deserializedFilters
    });
  }
  useSubscribeToBillingObservable(async () => {
    refreshRef.current();
  });
  useSetDocumentTitle(title);
  useEffect(() => {
    if (shouldRefresh.current) refreshRef.current();
  }, [urlState]);
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: () => refreshData(),
    renderLoader: () => /*#__PURE__*/_jsx(PageLoadingState, {
      header: title,
      loading: true
    }),
    renderError: _ref5 => {
      let {
        refresh
      } = _ref5;
      return /*#__PURE__*/_jsx(PageErrorState, {
        title: title,
        onRefresh: refresh
      });
    },
    renderChild: _ref6 => {
      let {
        data,
        loading,
        refresh
      } = _ref6;
      refreshRef.current = refresh;
      function refreshOnUrlStateChange() {
        shouldRefresh.current = true;
      }
      return /*#__PURE__*/_jsx(ActualAgreementsDashboard, {
        data: data,
        isRefreshing: loading,
        refresh: refresh,
        search: search,
        filters: deserializedFilters,
        onChangeFilter: (type, value) => {
          refreshOnUrlStateChange();
          changeUrlState({
            [type]: serializeFilter(type, value)
          });
        },
        onChangeSearch: search => {
          refreshOnUrlStateChange();
          changeUrlState({
            search
          });
        }
      });
    }
  });
}